import { createSlice } from '@reduxjs/toolkit'
import { FirestoreDatabase, storageRef } from '../index'
import { Collection } from 'constants/index'
import { signOut } from 'store/auth'
import { toast } from 'react-toastify'
import { first } from 'lodash'

export const initialState = {
  error: null,
  inProgress: false,
  sponsors: [],
  sponsor: null,
  currentPage: 0,
  pageSize: 10
}

const sponsorSlice = createSlice({
  name: 'sponsor',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setSponsors: (state, action) => {
      state.sponsors = action.payload
    },
    setSponsor: (state, action) => {
      state.sponsor = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.sponsors = []
      state.sponsor = null
      state.currentPage = 0
      state.pageSize = 10
    }
  }
})

export const getExistingSponsorWithEmail = async (email, conferenceId) => {
  const snapshot = await FirestoreDatabase.collection(Collection.SPONSORS)
    .where('email', '==', email)
    .where('conferenceId', '==', conferenceId)
    .get()

  const sponsors = snapshot.docs.map((doc) => ({ id: doc?.id, ...doc?.data() }))
  return sponsors?.length ? first(sponsors) : null
}

export const linkSubjectToSponsor = async (sponsorId, subjectId) => {
  await FirestoreDatabase.collection(Collection.SPONSORS).doc(sponsorId).update({
    subjectId: subjectId
  })
}

export const addSponsor = (payload, conferenceId) => async (dispatch) => {
  try {
    const { email } = payload
    dispatch(sponsorSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.SPONSORS)
      .where('email', '==', email)
      .where('conferenceId', '==', conferenceId)
      .get()

    if (snapshot.empty) {
      const doc = await FirestoreDatabase.collection(Collection.SPONSORS).add({
        ...payload,
        conferenceId: conferenceId
      })
      return doc.id
    } else {
      toast.error('Sponsor with this email already exists!')
    }
  } catch (err) {
    dispatch(sponsorSlice.actions.setProgress(false))
  } finally {
    dispatch(sponsorSlice.actions.setProgress(false))
  }
}

export const fetchSponsorById = (sponsorId) => async (dispatch) => {
  try {
    dispatch(sponsorSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.SPONSORS).doc(sponsorId).get()
    const e = snapshot.data()
    dispatch(
      sponsorSlice.actions.setSponsor({
        id: snapshot.id,
        ...e
      })
    )
    return snapshot.id
  } catch (err) {
    dispatch(sponsorSlice.actions.setError(err))
  } finally {
    dispatch(sponsorSlice.actions.setProgress(false))
  }
}

export const updateSponsorById = (sponsorId, payload, conferenceId) => async (dispatch) => {
  try {
    const { email } = payload
    dispatch(sponsorSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.SPONSORS)
      .where('email', '==', email)
      .where('conferenceId', '==', conferenceId)
      .get()

    const matchingSnapshot = snapshot.docs.filter((doc) => doc.id !== sponsorId)

    if (!matchingSnapshot.length) {
      await FirestoreDatabase.collection(Collection.SPONSORS)
        .doc(sponsorId)
        .update({
          ...payload
        })
      return sponsorId
    } else {
      toast.error('Sponsor with this email already exists!')
    }
  } catch (err) {
    dispatch(sponsorSlice.actions.setProgress(false))
    throw err
  } finally {
    dispatch(sponsorSlice.actions.setProgress(false))
  }
}

export const checkSponsorExistsInTalks = (sponsorId) => async (dispatch, getState) => {
  try {
    dispatch(sponsorSlice.actions.setProgress(true))
    const user = getState().auth.user
    const snapshot = await FirestoreDatabase.collection(Collection.TALKS)
      .where('conferenceId', '==', user?.conferenceId)
      .where('sponsorId', '==', sponsorId)
      .get()

    return !snapshot.empty
  } catch (err) {
    dispatch(sponsorSlice.actions.setError(err))
  } finally {
    dispatch(sponsorSlice.actions.setProgress(false))
  }
}

export const updateSponsorImage = async (sponsorId, imgPayload) => {
  await FirestoreDatabase.collection(Collection.SPONSORS)
    .doc(sponsorId)
    .update({
      ...imgPayload
    })
}

export const deleteSponsorById = (conferenceId, sponsorId) => async (dispatch) => {
  try {
    dispatch(sponsorSlice.actions.setProgress(true))
    const eventsSnapshot = await FirestoreDatabase.collection(Collection.EVENTS)
      .where('conferenceId', '==', conferenceId)
      .where('sponsorId', 'array-contains', sponsorId)
      .get()

    const talksSnapshot = await FirestoreDatabase.collection(Collection.TALKS)
      .where('conferenceId', '==', conferenceId)
      .where('sponsorId', '==', sponsorId)
      .get()

    if (eventsSnapshot.empty && talksSnapshot.empty) {
      const snapshot = await FirestoreDatabase.collection(Collection.SPONSORS).doc(sponsorId).get()
      const e = snapshot.data()
      await Promise.all([
        e?.adImageName &&
          storageRef.child(`${conferenceId}/${Collection.SPONSORS}/${e.adImageName}`).delete(),
        e?.logoImageName &&
          storageRef.child(`${conferenceId}/${Collection.SPONSORS}/${e.logoImageName}`).delete(),
        e?.brochureImageName &&
          storageRef.child(`${conferenceId}/${Collection.SPONSORS}/${e.brochureImageName}`).delete()
      ])
      await FirestoreDatabase.collection(Collection.SPONSORS).doc(sponsorId).delete()
      if (e.subjectId) {
        await FirestoreDatabase.collection(Collection.SUBJECTS).doc(e.subjectId).update({
          sponsorId: ''
        })
      }
      return true
    } else {
      toast.error(
        'This sponsor cannot be deleted because it is referenced in other event(s) or talk(s). To delete it, please remove it from all event(s) and talk(s) first.'
      )
    }
  } catch (err) {
    dispatch(sponsorSlice.actions.setError(err))
  } finally {
    dispatch(sponsorSlice.actions.setProgress(false))
  }
}

export const getSponsors = (conferenceId, callback) => {
  return FirestoreDatabase.collection(Collection.SPONSORS)
    .where('conferenceId', '==', conferenceId)
    .onSnapshot((documents) => {
      const sponsors = []
      documents.forEach((doc) => {
        sponsors.push({
          id: doc.id,
          ...doc.data()
        })
      })
      callback(sponsors)
    })
}

export const resetSponsor = () => (dispatch) => {
  dispatch(sponsorSlice.actions.setSponsor(null))
}

export default sponsorSlice
